import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

const ProfileGuru = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedSemester, setSelectedSemester] = useState("1");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedKelas, setSelectedKelas] = useState("");
  const [selectedRombel, setSelectedRombel] = useState("");

  /* ================= ROLE ================= */
  const isGuruKelas = user?.jabatan === "Guru Kelas";
  const isGuruMapel = user?.jabatan === "Guru Mapel";

  /* ================= AMBIL PROFIL ================= */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    axios
      .get(`${process.env.REACT_APP_API_URL}api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      .then((res) => setUser(res.data))

      .catch(() => navigate("/login"));
  }, [navigate]);
  /* ================= AMBIL NILAI RAPOR ================= */
  useEffect(() => {
    if (!user?.jabatan) return;

    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}api/nilaiRapor/all`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        let filtered = [];

        if (isGuruKelas) {
          filtered = res.data.filter(
            (i) => i.kelas === user.kelas && i.rombel === user.rombel,
          );
        }

        if (isGuruMapel) {
          filtered = res.data.filter((i) => i.mapel === user.mapel);
        }

        setFilteredData(filtered);
      } catch (err) {
        setError("Gagal mengambil data nilai");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, isGuruKelas, isGuruMapel, navigate]);

  /* ================= FILTER UTAMA ================= */
  const filteredByAll = useMemo(() => {
    return filteredData.filter((i) => {
      if (i.semester !== selectedSemester) return false;
      if (selectedYear && i.tahun !== selectedYear) return false;

      if (isGuruMapel) {
        if (selectedKelas && i.kelas !== selectedKelas) return false;
        if (selectedRombel && i.rombel !== selectedRombel) return false;
      }

      return true;
    });
  }, [
    filteredData,
    selectedSemester,
    selectedYear,
    selectedKelas,
    selectedRombel,
    isGuruMapel,
  ]);

  /* ================= LIST OPTION FILTER ================= */
  const allYears = useMemo(
    () => [...new Set(filteredData.map((i) => i.tahun))].sort(),
    [filteredData],
  );

  const allKelas = useMemo(
    () => [...new Set(filteredData.map((i) => i.kelas))].sort(),
    [filteredData],
  );

  const allRombel = useMemo(
    () =>
      [
        ...new Set(
          filteredData
            .filter((i) => (selectedKelas ? i.kelas === selectedKelas : true))
            .map((i) => i.rombel),
        ),
      ].sort(),
    [filteredData, selectedKelas],
  );

  const mapels = useMemo(
    () => [...new Set(filteredByAll.map((i) => i.mapel))],
    [filteredByAll],
  );

  /* ================= GROUP DATA TABEL ================= */
  const groupedTable = useMemo(() => {
    const acc = {};

    filteredByAll.forEach((item) => {
      item.nilai.forEach((siswa) => {
        if (!acc[siswa.name]) acc[siswa.name] = {};
        acc[siswa.name][item.mapel] =
          typeof siswa.finalScore === "number"
            ? Math.round(siswa.finalScore)
            : "-";
      });
    });

    return acc;
  }, [filteredByAll]);

  /* ================= RANKING (GURU KELAS) ================= */
  const rankingData = useMemo(() => {
    if (!isGuruKelas) return [];

    return Object.entries(groupedTable)
      .map(([name, subjects]) => {
        let total = 0;
        let count = 0;

        mapels.forEach((mapel) => {
          const val = Number(subjects?.[mapel]);
          if (!isNaN(val)) {
            total += val;
            count++;
          }
        });

        return {
          name,
          avg: count ? (total / count).toFixed(2) : "-",
        };
      })
      .sort((a, b) => b.avg - a.avg)
      .map((item, index) => ({
        ...item,
        rank: index + 1,
      }));
  }, [groupedTable, mapels, isGuruKelas]);

  /* ================= LOADING ================= */
  if (loading) return <div className="text-center py-20">Loading...</div>;
  if (error) return <div className="text-center py-20">{error}</div>;

  /* ================= JSX ================= */
  return (
    <div className="px-4 py-10 max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold text-center mb-8">Daftar Nilai</h2>

      {/* FILTER */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          className="border p-2 rounded"
        >
          <option value="">Semua Tahun</option>
          {allYears.map((y) => (
            <option key={y}>{y}</option>
          ))}
        </select>

        <select
          value={selectedSemester}
          onChange={(e) => setSelectedSemester(e.target.value)}
          className="border p-2 rounded"
        >
          <option value="1">Semester 1</option>
          <option value="2">Semester 2</option>
        </select>

        {/* FILTER KELAS & ROMBEL (GURU MAPEL) */}
        {isGuruMapel && (
          <>
            <select
              value={selectedKelas}
              onChange={(e) => {
                setSelectedKelas(e.target.value);
                setSelectedRombel("");
              }}
              className="border p-2 rounded"
            >
              <option value="">Pilih Kelas</option>
              {allKelas.map((k) => (
                <option key={k}>{k}</option>
              ))}
            </select>

            <select
              value={selectedRombel}
              onChange={(e) => setSelectedRombel(e.target.value)}
              className="border p-2 rounded"
            >
              <option value="">Pilih Rombel</option>
              {allRombel.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* TABEL */}
      <div className="overflow-x-auto">
        <table className="min-w-full border border-slate-400 text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="border p-2">Siswa</th>
              {mapels.map((m) => (
                <th key={m} className="border p-2">
                  {m}
                </th>
              ))}
              {isGuruKelas && (
                <>
                  <th className="border p-2">Rata-rata</th>
                  <th className="border p-2">Peringkat</th>
                </>
              )}
            </tr>
          </thead>

          <tbody>
            {Object.entries(groupedTable).map(([name, subjects]) => {
              const rank = rankingData.find((r) => r.name === name);

              return (
                <tr key={name}>
                  <td className="border p-2 font-semibold">{name}</td>

                  {mapels.map((m) => (
                    <td key={`${name}-${m}`} className="border p-2 text-center">
                      {subjects?.[m] ?? "-"}
                    </td>
                  ))}

                  {isGuruKelas && (
                    <>
                      <td className="border p-2 text-center">
                        {rank?.avg ?? "-"}
                      </td>
                      <td className="border p-2 text-center">
                        {rank?.rank ?? "-"}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MENU */}
      <div className="flex justify-center mt-10">
        <h1 className="font-bold text-2xl">Menu Tambahan</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 mt-5">
        {[
          { label: "Absensi", to: `/absensi/${id}` },
          { label: "TP", to: `/cpadmin/${id}` },
          { label: "Kokurikuler", to: `/Kokurikuler/${id}` },
          { label: "Rapor", to: `/guru/rapor/${id}` },
          { label: "Jurnal", to: `/jurnal/${id}` },
          { label: "RPP", to: `/RPP` },
        ].map((item) => (
          <Link key={item.label} to={item.to} className="flex justify-center">
            <div className="h-36 w-36 bg-white rounded-xl shadow-lg flex items-center justify-center font-bold text-xl hover:scale-105 transition">
              {item.label}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ProfileGuru;
